import { createContext, useContext, useMemo, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ViewMode } from './ViewModeContext.helpers'
import { viewModeFromPathname, buildViewModePath } from './ViewModeContext.helpers'
import { getProjectIdFromPath } from '../utils/active-project'

interface ViewModeContextType {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined)

interface ViewModeProviderProps {
  children: ReactNode
}

export const ViewModeProvider = ({ children }: ViewModeProviderProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  // Extract viewMode from current route using useMemo
  const viewMode = useMemo((): ViewMode => {
    // Handle HashRouter - extract path from hash
    const path = location.hash ? location.hash.slice(1) : location.pathname
    // Strip query params before splitting path
    const pathWithoutQuery = path.split('?')[0]
    // Derive view mode from /sacrifice/:projectId/:mode; null means off-route → default 'global'
    return viewModeFromPathname(pathWithoutQuery) ?? 'global'
  }, [location.pathname, location.hash])

  // Navigate to the same project at a different view mode, preserving query params
  const setViewMode = (mode: ViewMode) => {
    // Handle HashRouter - extract path from hash
    const path = location.hash ? location.hash.slice(1) : location.pathname
    // Separate path and query params
    // In HashRouter, query params can be either in the hash itself OR in location.search
    const [pathWithoutQuery, hashQueryString] = path.split('?')
    // Use hashQueryString if found, otherwise fall back to location.search (without the leading ?)
    const queryString = hashQueryString || (location.search ? location.search.slice(1) : '')
    const projectId = getProjectIdFromPath(pathWithoutQuery)
    if (projectId) {
      const newPath = buildViewModePath(projectId, mode)
      navigate(queryString ? `${newPath}?${queryString}` : newPath)
    }
  }

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode }}>
      {children}
    </ViewModeContext.Provider>
  )
}

export const useViewMode = () => {
  const context = useContext(ViewModeContext)
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider')
  }
  return context
}
